import { useAppStore } from "../../../providers/AppStore";
import { countPendingSystems } from "../components/healthOverview/pendingApprovals";
import { resolveClientGender, useClientGender } from "./useClientGender";

type SavedSnackbarArgs = {
  // The report refetched right after the save — the pending count and the
  // gender both come from it, so the prompt reflects what the save just did
  // rather than the Redux state this render still holds.
  report: any;
  // Title of the review prompt, naming what was saved ("Biomarkers saved",
  // "InBody results imported").
  reviewTitle: string;
  // The plain success toast shown when there is nothing to review.
  fallback: { title: string; message: string };
};

/**
 * The snackbar shown after new biomarker data lands, shared by every way of
 * getting that data in (lab-report extractor, InBody sync).
 *
 * When the save moved system scores into "pending approval", a self-dismissing
 * toast would drop the user at a screen whose scores changed silently — so we
 * confirm the save and hand over the review in the same prompt. "Review scores"
 * is primary because that is where we want them to go; "Review later" just
 * closes, leaving the pending-approvals banner as the way back. With nothing
 * pending — or on a screen with no review modal to open — the plain toast stays.
 *
 * Pass `onReviewScores` from the host screen (it owns the Review health scores
 * modal); omitting it opts out of the prompt entirely.
 */
export const useBiomarkersSavedSnackbar = (onReviewScores?: () => void) => {
  const {
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
  } = useAppStore();
  const gender = useClientGender();

  return ({ report, reviewTitle, fallback }: SavedSnackbarArgs) => {
    const freshGender = resolveClientGender(report?.dashboard?.gender);
    const hasPendingScores =
      countPendingSystems(
        report?.score?.systems,
        freshGender === "unknown" ? gender : freshGender
      ) > 0;

    ASSetOpenSnackBar(true);
    if (onReviewScores && hasPendingScores) {
      ASSetSnackBarMessage({
        title: reviewTitle,
        message:
          "The new results affect this client’s health scores. Review them now to update what the client sees, or come back to it later from the pending-approvals banner.",
      });
      ASSetSnackBarActions({
        tone: "success",
        confirmLabel: "Review scores",
        cancelLabel: "Review later",
        onConfirm: onReviewScores,
      });
      ASSetTypeSnackBar("warning");
      return;
    }
    ASSetSnackBarMessage(fallback);
    ASSetTypeSnackBar("success");
  };
};
